import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

/**
 * Admin Adjustment History Component
 * Displays audit trail of all token quota adjustments
 */
export const AdminAdjustmentHistory = () => {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'manual_increase', 'manual_decrease', 'override'
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const fetchAdjustments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        adjustmentType: filter === 'all' ? '' : filter,
        sortBy: sortBy,
        order: sortOrder,
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage
      });

      const response = await fetch(`/api/admin/llm/adjustment-history?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch adjustments');

      const data = await response.json();
      setAdjustments(data.adjustments || []);
      setError('');
    } catch (err) {
      setError(err.message);
      console.error('History error:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, page, sortBy, sortOrder]);

  useEffect(() => {
    fetchAdjustments();
  }, [fetchAdjustments]);

  const toggleExpandRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAdjustmentBadge = (type) => {
    const badges = {
      manual_increase: { bg: 'bg-green-100', text: 'text-green-700', label: 'Increase' },
      manual_decrease: { bg: 'bg-red-100', text: 'text-red-700', label: 'Decrease' },
      override: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Override' },
      quota_reset: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Reset' }
    };
    const badge = badges[type] || { bg: 'bg-gray-100', text: 'text-gray-700', label: type };
    return badge;
  };

  const formatNumber = (num) => {
    return (num || 0).toLocaleString();
  };

  if (loading) {
    return <div className="adjustment-history p-4">Loading adjustment history...</div>;
  }

  return (
    <div className="adjustment-history space-y-4">
      {/* Filters */}
      <div className="filters flex gap-2 items-center">
        <label className="text-sm font-medium">Filter:</label>
        <select
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-1 border rounded text-sm"
        >
          <option value="all">All Adjustments</option>
          <option value="manual_increase">Increases</option>
          <option value="manual_decrease">Decreases</option>
          <option value="override">Overrides</option>
          <option value="quota_reset">Resets</option>
        </select>

        <label className="text-sm font-medium ml-4">Sort by:</label>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-1 border rounded text-sm"
        >
          <option value="created_at">Date</option>
          <option value="tokens_added">Tokens</option>
          <option value="admin_id">Admin</option>
        </select>

        <button
          onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
          className="px-2 py-1 border rounded text-sm hover:bg-gray-50"
        >
          {sortOrder === 'ASC' ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded text-sm">{error}</div>
      )}

      {/* Adjustments Table */}
      <div className="table-container bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Date</th>
              <th className="px-4 py-3 text-left font-medium">User</th>
              <th className="px-4 py-3 text-left font-medium">Admin</th>
              <th className="px-4 py-3 text-left font-medium">Type</th>
              <th className="px-4 py-3 text-left font-medium">Tokens</th>
              <th className="px-4 py-3 text-left font-medium">Balance</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {adjustments.length > 0 ? (
              adjustments.map((adjustment) => {
                const isExpanded = expandedRow === adjustment.id;
                const badge = getAdjustmentBadge(adjustment.adjustment_type);
                
                return (
                  <React.Fragment key={adjustment.id}>
                    <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpandRow(adjustment.id)}>
                      <td className="px-4 py-3">{formatDate(adjustment.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{adjustment.user_business_name}</div>
                        <div className="text-xs text-gray-500">{adjustment.user_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{adjustment.admin_name}</div>
                        <div className="text-xs text-gray-500">{adjustment.admin_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {adjustment.tokens_added > 0 ? '+' : ''}{formatNumber(adjustment.tokens_added)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{formatNumber(adjustment.new_balance)}</div>
                        <div className="text-xs text-gray-500">
                          from {formatNumber(adjustment.previous_balance)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isExpanded ? (
                          <ChevronUpIcon className="w-4 h-4 mx-auto" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4 mx-auto" />
                        )}
                      </td>
                    </tr>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <tr className="bg-gray-50 border-b">
                        <td colSpan="7" className="px-4 py-4">
                          <div className="space-y-2 text-sm">
                            <div>
                              <strong>Reason:</strong>
                              <p className="text-gray-700 mt-1 p-2 bg-white rounded border">{adjustment.reason}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <strong>Previous Balance:</strong>
                                <p className="text-gray-600">{formatNumber(adjustment.previous_balance)} tokens</p>
                              </div>
                              <div>
                                <strong>New Balance:</strong>
                                <p className="text-gray-600">{formatNumber(adjustment.new_balance)} tokens</p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  No adjustments found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination flex gap-2 justify-center items-center">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="px-3 py-1 border rounded text-sm disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm">Page {page}</span>
        <button
          onClick={() => setPage(page + 1)}
          disabled={adjustments.length < itemsPerPage}
          className="px-3 py-1 border rounded text-sm disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AdminAdjustmentHistory;
